import { Directive, HostBinding, Input } from '@angular/core';

/**
 * enums
 */
import { FormFieldFeedbackStatus, IFormFieldFeedbackStatus } from './enums/form-field-feedback-type';
import { FormFieldFeedbackProvider, IFormFieldFeedbackProvider } from './enums/form-field-feedback-provider';

/**
 * @directive FormFieldFeedbackDirective
 * @description
 * **Angular directive for displaying form field feedback** with customizable providers and styling.
 *
 * This directive provides a flexible way to display validation feedback for form fields.
 * It supports both default feedback (block display) and tooltip feedback, with automatic
 * Bootstrap class generation based on the feedback type and provider.
 *
 * **Key Features:**
 * - 🎯 **Flexible Feedback Types** - Valid and Invalid feedback support
 * - 🎪 **Multiple Providers** - Default and Tooltip feedback providers
 * - 🏷️ **Automatic Styling** - Bootstrap feedback class generation
 * - 🛡️ **Type Safety** - Type-safe feedback type and provider handling
 * - 🎨 **Bootstrap Integration** - Uses Bootstrap feedback classes
 * - 🔄 **Dynamic Classes** - Automatic class binding based on state
 *
 * **Common Use Cases:**
 * - Form validation feedback display
 * - Real-time validation messages
 * - Tooltip-style feedback
 * - Custom validation feedback
 * - Form field status indicators
 *
 * @example
 * <!-- Default feedback provider -->
 * <div appFormFieldFeedback="invalid">
 *   This field is required.
 * </div>
 *
 * @example
 * <!-- Tooltip feedback provider -->
 * <div appFormFieldFeedback="valid" feedbackProvider="tooltip">
 *   Email format is correct.
 * </div>
 */
@Directive({
  selector: '[appFormFieldFeedback]',
})
export class FormFieldFeedbackDirective {

  /**
   * @property type
   * @description
   * **Determines the feedback type** (valid or invalid) for the form field.
   *
   * This required property sets the feedback type and determines the appropriate
   * Bootstrap class and styling. It accepts `FormFieldFeedbackStatus` enum values
   * and is used as the primary input for the directive.
   *
   * @type {FormFieldFeedbackStatus}
   * @required true
   * @example
   * <!-- Valid feedback -->
   * <div appFormFieldFeedback="valid">
   *   Email format is correct.
   * </div>
   *
   * @example
   * <!-- Invalid feedback -->
   * <div appFormFieldFeedback="invalid">
   *   This field is required.
   * </div>
   */
 @Input({ 
    required: true,
    alias: 'appFormFieldFeedback',
  })
  public status!: IFormFieldFeedbackStatus

  /**
   * @property provider
   * @description
   * **Chooses the feedback display provider** (tooltip or default block display).
   *
   * This property determines how the feedback is displayed. It accepts `IFormFieldFeedbackProvider`
   * enum values and defaults to `Default` (block display). The provider affects the CSS class
   * suffix and visual presentation of the feedback.
   *
   * @type {IFormFieldFeedbackProvider}
   * @default FormFieldFeedbackProvider.Default
   * @example
   * <!-- Default provider (block display) -->
   * <div appFormFieldFeedback="invalid">
   *   This field is required.
   * </div>
   *
   * @example
   * <!-- Tooltip provider -->
   * <div appFormFieldFeedback="valid" feedbackProvider="tooltip">
   *   Email format is correct.
   * </div>
   */
  @Input({ alias: 'feedbackProvider' })
  public provider: IFormFieldFeedbackProvider = FormFieldFeedbackProvider.Default

  /**
   * @property valid
   * @description
   * **Returns true if the feedback type is Valid**.
   *
   * This getter provides a convenient way to check if the current feedback type
   * represents a valid state. It's useful for conditional styling or logic
   * based on the feedback type.
   *
   * @type {boolean}
   * @example
   * <!-- Template usage -->
   * <div appFormFieldFeedback="valid" #feedback>
   *   <span *ngIf="feedback.valid">✓ Valid input</span>
   * </div>
   *
   * @example
   * <!-- Component usage -->
   * @ViewChild('feedback') feedback!: FormFieldFeedbackDirective;
   *
   * if (this.feedback.valid) {
   *   console.log('Feedback is valid');
   * }
   */
  public get valid(): boolean {
    return this.status === FormFieldFeedbackStatus.Valid
  }

  /**
   * @property invalid
   * @description
   * **Returns true if the feedback type is Invalid**.
   *
   * This getter provides a convenient way to check if the current feedback type
   * represents an invalid state. It's useful for conditional styling or logic
   * based on the feedback type.
   *
   * @type {boolean}
   * @example
   * <!-- Template usage -->
   * <div appFormFieldFeedback="invalid" #feedback>
   *   <span *ngIf="feedback.invalid">✗ Invalid input</span>
   * </div>
   *
   * @example
   * <!-- Component usage -->
   * @ViewChild('feedback') feedback!: FormFieldFeedbackDirective;
   *
   * if (this.feedback.invalid) {
   *   console.log('Feedback is invalid');
   * }
   */
  public get invalid(): boolean {
    return this.status === FormFieldFeedbackStatus.Invalid
  }

  /**
   * @property typeValue
   * @description
   * **Maps the feedback type enum to a string value** ('valid' or 'invalid').
   *
   * This getter converts the `FormFieldFeedbackStatus` enum to a string representation
   * that can be used for CSS class generation and other string-based operations.
   * It provides a clean way to get the type as a string for Bootstrap class names.
   *
   * @type {'valid' | 'invalid'}
   * @example
   * <!-- Template usage -->
   * <div appFormFieldFeedback="valid" #feedback>
   *   <span>Type: {{ feedback.typeValue }}</span>
   * </div>
   *
   * @example
   * <!-- Component usage -->
   * @ViewChild('feedback') feedback!: FormFieldFeedbackDirective;
   *
   * console.log('Feedback type:', this.feedback.typeValue);
   * // Output: 'valid' or 'invalid'
   */
  public get typeValue(): 'valid' | 'invalid' {
    
    switch(this.status) {
      case FormFieldFeedbackStatus.Valid:
        return 'valid';
      case FormFieldFeedbackStatus.Invalid:
      default:
        return 'invalid'
    }

  }

  /**
   * @property _feedbackProviderClassSuffix
   * @description
   * **Returns the CSS class suffix** ('tooltip' or 'feedback') based on the provider.
   *
   * This private getter determines the appropriate CSS class suffix based on the
   * feedback provider. It's used internally to generate the correct Bootstrap
   * feedback class name.
   *
   * @type {'tooltip' | 'feedback'}
   * @private
   * @example
   * // Internal usage for class generation
   * const suffix = this._feedbackProviderClassSuffix;
   * // Returns 'tooltip' or 'feedback'
   */
  private get _feedbackProviderClassSuffix(): 'tooltip' | 'feedback' {
    return this.provider === FormFieldFeedbackProvider.Tooltip ? 'tooltip' : 'feedback'
  }

  /**
   * @property _hostClassList
   * @description
   * **Binds the correct Bootstrap feedback class** to the host element.
   *
   * This private getter generates and binds the appropriate Bootstrap feedback
   * class based on the feedback type and provider. It combines the type value
   * with the provider suffix to create classes like 'valid-tooltip' or 'invalid-feedback'.
   *
   * @type {string}
   * @private
   * @example
   * <!-- Generated classes -->
   * <!-- For valid + tooltip: 'valid-tooltip' -->
   * <!-- For invalid + default: 'invalid-feedback' -->
   * <!-- For valid + default: 'valid-feedback' -->
   * <!-- For invalid + tooltip: 'invalid-tooltip' -->
   */
  @HostBinding('class')
  private get _hostClassList(): string {
    return `${this.typeValue}-${this._feedbackProviderClassSuffix}`
  }

  /**
   * @constructor
   * @description
   * **Initializes the form field feedback directive**.
   *
   * The constructor is minimal as this directive primarily relies on input properties
   * and computed getters for its functionality. No dependencies are injected.
   *
   * @example
   * // Automatically instantiated by Angular's dependency injection
   * constructor() { }
   */
  constructor() { }

}
